<template>
    <PageWrapper :breadcrumb-links="breadcrumbLinks">
        <main
            class="mx-auto w-full max-w-7xl px-2 lg:pl-[calc(theme(spacing.64)+theme(spacing.4))]"
        >
            <StickyPanel
                :top-default="0"
                :top-responsive="10"
            >
                <FilterToolbar
                    :key="`template-filter-toolbar-` + pager.key"
                    :page-data="pageData"
                    :buttons="toolbarConfig.buttons"
                />
                <OrderBar
                    :current="pageData.sortBy"
                    :order-options="pageData.sortOptions"
                />
            </StickyPanel>
            <BatchImportSteps :steps="wizzardSteps" />
            <AlertBox
                v-if="pageData.data.items.length === 0 && pageData.loading === false"
                state="warning"
                :icon="MdiInformation"
                :title="t('TemplateVersions')"
            >
                <p class="py-1 text-sm">{{ t('notFoundText') }}</p>
            </AlertBox>
            <div
                v-if="pageData.data.items.length > 0"
                :key="`page-data-list-${pageDataListKey}`"
                class="divide-y divide-gray-200"
            >
                <div
                    v-for="templateVersion in pageData.data.items"
                    :key="templateVersion.templateVersionId"
                    class="group/template"
                >
                    <div
                        class="flex cursor-pointer items-center justify-start gap-4 py-2 transition duration-150 ease-in-out hover:bg-gray-100"
                        @click="nextStep(templateVersion)"
                    >
                        <div class="flex-none px-2 text-center">
                            <MdiFileDocumentCheck
                                v-if="templateVersion.state === 'APPROVED'"
                                class="mx-auto inline h-7 w-7 text-green-400"
                            />
                            <MdiFileDocumentAlert
                                v-if="templateVersion.state === 'DRAFT'"
                                class="mx-auto inline h-7 w-7 text-red-400"
                            />
                        </div>

                        <div class="flex grow flex-col gap-2">
                            <p class="truncate text-sm font-medium text-wrap text-blue-600">
                                {{ templateVersion.changeLog }}
                            </p>
                            <p
                                v-if="templateVersion.subject.length > 0"
                                class="truncate text-xs font-medium text-wrap text-gray-500"
                            >
                                <em>{{ templateVersion.subject }}</em>
                            </p>
                            <div class="text-sm text-gray-500 sm:flex sm:items-center sm:gap-2">
                                <div>
                                    {{ t('templateVersion') }}:&nbsp;<strong>{{
                                        templateVersion.version
                                    }}</strong>
                                </div>
                                <div class="hidden sm:inline-block">-</div>
                                <div>
                                    {{ getTemplateLang(templateVersion.langCode)?.name || '?' }}
                                </div>
                            </div>
                        </div>
                        <div class="hidden md:flex md:flex-col md:items-end md:justify-start">
                            <AuditInfo :item="templateVersion" />
                        </div>

                        <div class="flex-none px-2">
                            <MdiChevronRight class="h-5 w-5 text-gray-400" />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </PageWrapper>
    <div
        class="fixed right-0 bottom-0 left-1 z-2000 flex h-16 items-center justify-center bg-white lg:left-64"
    >
        <Pager
            :key="pager.key"
            :current="pager.current"
            :end="pager.end"
        />
    </div>
    <Spinner
        v-if="pageData.loading === true"
        type="dots"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="t('loading')"
        :inline="false"
        :pulse="true"
    />
</template>

<script setup>
import { reactive, ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AlertBox from '../ui/AlertBox.vue';
import { t, getTemplateLang } from '../composables/i18n';
import { getTemplateVersions, getTemplate, isEmptyModel } from '../composables/useTemplates';
import Pager from '../ui/Pager.vue';
import { makePager } from '../composables/usePaginator';
import PageWrapper from '../ui/PageWrapper.vue';
import BatchImportSteps from '../components/BatchImportSteps.vue';
import {
    MdiChevronRight,
    MdiFileDocumentCheck,
    MdiFileDocumentAlert,
    MdiInformation,
} from 'materialdesignicons-vue3/icons/';
import Spinner from '../ui/Spinner.vue';
import FilterToolbar from '../ui/FilterToolbar.vue';
import OrderBar from '../ui/OrderBar.vue';
import AuditInfo from '../ui/AuditInfo.vue';
import StickyPanel from '../ui/StickyPanel.vue';
import { reloadPageData } from '../composables/usePages';
import { parseSortBy, sortByChanged } from '../composables/useSortBy';
import { getRndKey } from '../composables/utils';

const route = useRoute();
const router = useRouter();
const templateId = route.params.templateid || '0';
const templateModel = reactive((await getTemplate(templateId)) || {});
const pageDataListKey = ref(getRndKey(17));

if (isEmptyModel(templateModel)) {
    document.location.href = '/404';
}

const sortDefault = ['version:desc'];

const pageData = reactive({
    data: {
        items: [],
        totalCount: 0,
    },
    currentPage: parseInt(route.params.page) || 1,
    perPage: 15,
    search: route.query.search || '',
    type: route.query.type || '',
    loading: true,
    useTags: false,
    tags: {
        items: [],
        totalCount: 0,
    },
    selectedTags: [],
    sortDefault,
    sortBy: parseSortBy(route.query.sortBy, sortDefault),
    sortOptions: [
        /* { name: 'Id', value: 'id' }, */
        { name: t('sortVersion'), value: 'version' },
        { name: t('sortLangCode'), value: 'langCode' },
        { name: t('sortSubject'), value: 'subject' },
        { name: t('sortState'), value: 'state' },
        { name: t('sortCreated'), value: 'created' },
        { name: t('sortUpdated'), value: 'updated' },
    ],
    filters: {},
});
const pager = reactive(makePager(pageData.currentPage, pageData.data.totalCount, 4));

const breadcrumbLinks = reactive([
    {
        name: 'dashboard',
        title: t('Dashboard'),
    },
    {
        name: 'templates',
        title: t('Templates'),
    },
    {
        name: 'template-versions',
        title: templateModel.name,
        final: true,
    },
]);

const nextStep = (templateVersion) => {
    router.push({
        name: 'batch-import-3',
        params: {
            templateid: templateId,
            templateversionid: templateVersion.templateVersionId,
        },
    });
};

const toolbarConfig = reactive({
    buttons: [],
});

const wizzardSteps = [
    {
        id: '01',
        title: t('Templates'),
        path: {
            name: 'batch-import-1',
        },
        status: 'complete',
    },
    {
        id: '02',
        title: t('TemplateVersions'),
        status: 'current',
    },
    {
        id: '03',
        title: t('buttonSchema'),
        status: 'upcoming',
    },
    {
        id: '04',
        title: t('processFiles'),
        status: 'upcoming',
    },
];

const refreshPageData = async () => {
    await reloadPageData(pageData, getTemplateVersions, pager, {
        templateId: templateId,
    });
    breadcrumbLinks[2].title = `${t('TemplateVersions')}: ${templateModel.name} (${pageData.data.totalCount})`;
    pageDataListKey.value = getRndKey(17);
};

watch(templateModel, () => {
    breadcrumbLinks[2]['title'] = `${t('TemplateVersions')}: ${templateModel.name}`;
});

watch(
    () => route.params.page,
    async (newPage, oldPage) => {
        if (newPage !== oldPage) {
            //console.log('Route changed', newPage, oldPage);
            pageData.currentPage = parseInt(newPage) || 1;
            refreshPageData();
        }
    },
);

watch(
    () => route.query,
    async (newQuery, oldQuery) => {
        const filterKeys = Object.keys(pageData.filters);
        const searchChanged = newQuery.search !== oldQuery.search;
        const filtersChanged = filterKeys.some((key) => newQuery[key] !== oldQuery[key]);
        const sortChanged = sortByChanged(newQuery, oldQuery);
        if (searchChanged || filtersChanged || sortChanged) {
            //console.log('Query changed', newQuery, oldQuery);
            pageData.search = newQuery.search || '';
            for (const key of filterKeys) {
                pageData.filters[key].active = newQuery[key] || '';
            }
            if (sortChanged) {
                pageData.sortBy = parseSortBy(newQuery.sortBy, pageData.sortDefault);
            }
            pageData.currentPage = 1;
            await refreshPageData();
        }
    },
);

onMounted(async () => {
    await refreshPageData();
});
</script>
